/*= ========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== */

import Vue from 'vue'
import Router from 'vue-router'
import store from '../store/index'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    // =============================================================================
    // Theme Routes
    // =============================================================================
    {
      path: '/',
      name: 'dashboard',
      meta: {
        requiresAuth: true,
        resource: 'view_dashboard',
        breadcrumb: [
          {
            text: 'common.dashboard',
            active: true,
          },
        ],
        main: true,
      },
      component: () => import('@ps_views/Dashboard.vue'),
    },
    {
      path: '/profile',
      name: 'profile',
      meta: {
        requiresAuth: true,
        breadcrumb: [
          {
            text: 'payments.profile',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/user-settings/UserSettings.vue'),
    },
    {
      path: '/apps',
      name: 'apps',
      meta: {
        requiresAuth: true,
        resource: 'view_apps',
        breadcrumb: [
          {
            text: 'common.apps',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/apps/Apps.vue'),
    },
    {
      path: '/apps/edit/:appHash',
      name: 'app-edit',
      meta: {
        requiresAuth: true,
        resource: 'view_app_details',
        breadcrumb: [
          {
            text: 'logs.app',
          },
          {
            text: 'apps.edit',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/apps/edit/AppEdit.vue'),
    },
    {
      path: '/audiences',
      name: 'audiences',
      meta: {
        requiresAuth: true,
        resource: 'view_audiences',
        breadcrumb: [
          {
            text: 'common.audiences',
            active: true,
          },
        ],
        loading: false,
      },
      component: () => import('@ps_views/Audiences.vue'),
    },
    {
      path: '/payments',
      name: 'payments',
      meta: {
        requiresAuth: true,
        resource: 'view_payments',
        breadcrumb: [
          {
            text: 'usersettings.payment',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/Payments.vue'),
    },
    {
      path: '/statistics/revenue',
      name: 'revenue-statistics',
      meta: {
        requiresAuth: true,
        resource: 'view_revenue_stats',
        breadcrumb: [
          {
            text: 'common.statistics',
          },
          {
            text: 'common.revenueStatistics',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/RevenueStatistics.vue'),
    },
    {
      path: '/statistics/survey',
      name: 'survey-statistics',
      meta: {
        requiresAuth: true,
        resource: 'view_survey_stats',
        breadcrumb: [
          {
            text: 'common.statistics',
          },
          {
            text: 'common.surveyStatistics',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/SurveyStatistics.vue'),
    },
    {
      path: '/statistics',
      redirect: '/statistics/revenue',
    },
    {
      path: '/logs/click-log',
      name: 'click-log',
      meta: {
        requiresAuth: true,
        resource: 'view_click_logs',
        breadcrumb: [
          {
            text: 'common.logs',
          },
          {
            text: 'common.clickLog',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/logs/ClickLog.vue'),
    },
    {
      path: '/logs/postback-log',
      name: 'postback-log',
      meta: {
        requiresAuth: true,
        resource: 'view_postback_logs',
        breadcrumb: [
          {
            text: 'common.logs',
          },
          {
            text: 'common.postbackLog',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/logs/PostbackLog.vue'),
    },
    {
      path: '/logs/transaction-log',
      name: 'transaction-log',
      meta: {
        requiresAuth: true,
        resource: 'view_transaction_logs',
        breadcrumb: [
          {
            text: 'common.logs',
          },
          {
            text: 'common.transactionLog',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/logs/TransactionLog.vue'),
    },
    {
      path: '/developers/integration',
      name: 'developers-integration',
      meta: {
        requiresAuth: true,
        resource: 'view_integration',
        breadcrumb: [
          {
            text: 'common.developer',
          },
          {
            text: 'apps.integration',
            active: true,
          },
        ],
      },
      component: () => import('@ps_views/developers/Integration.vue'),
    },
    // Public/Auth Pages
    {
      path: '/pages/login',
      name: 'page-login',
      meta: {
        loading: false,
        layout: 'full',
      },
      component: () => import('@ps_views/pages/Login.vue'),
    },
    {
      path: '/pages/register',
      name: 'page-register',
      meta: {
        loading: false,
        layout: 'full',
      },
      component: () => import('@ps_views/pages/Register.vue'),
    },
    {
      path: '/pages/forgot-password',
      name: 'page-forgot-password',
      meta: {
        loading: false,
        layout: 'full',
      },
      component: () => import('@ps_views/pages/ForgotPassword.vue'),
    },
    {
      path: '/pages/set-password',
      name: 'page-set-password',
      meta: {
        loading: false,
        layout: 'full',
      },
      component: () => import('@ps_views/pages/SetPassword.vue'),
    },
    {
      path: '/pages/error-404',
      name: 'page-error-404',
      meta: {
        layout: 'full',
        loading: false,
      },
      component: () => import('@ps_views/pages/Error404.vue'),
    },
    {
      path: '/pages/terms-and-conditions',
      name: 'page-terms-and-conditions',
      meta: {
        loading: false,
        layout: 'full',
      },
      component: () => import('@ps_views/pages/TermsAndConditions.vue'),
    },
    {
      path: '*',
      redirect: '/pages/error-404',
    },
  ],
})

// Navigation Guard
router.beforeEach(async (to, from, next) => { // Added async
  const isUserLoggedIn = store.getters['auth/isLoggedIn']

  const redirectPagesIfLoggedIn = ['page-login', 'page-register', 'page-forgot-password']

  // 1. Redirect if already logged in
  if (redirectPagesIfLoggedIn.indexOf(to.name) >= 0) {
    if (isUserLoggedIn && (to.name !== 'page-login' || window.location.href.indexOf('alk=') === -1)) {
      return next({ name: 'dashboard' })
    }
    return next()
  }

  // 2. Handle Loading UI
  if (to.meta.loading !== false) {
    router.app.showLoading()
  }

  // 3. Check Authentication Requirement
  if (to.meta.requiresAuth !== true) {
    router.app.hideLoading()
    return next()
  }

  if (!isUserLoggedIn) {
    router.app.hideLoading()
    return next({ path: '/pages/login', query: { to: to.path } })
  }

  // If logged in but permissions are empty, we are likely in a page refresh state.
  // Wait for the auth/init action (which calls fetchCurrentUser) to finish.
  if (isUserLoggedIn && store.getters['auth/userPermissions'].length === 0) {
    try {
      await store.dispatch('auth/init') // Assuming this is your init action name
    } catch (e) {
      router.app.hideLoading()
      return next({ path: '/pages/login' })
    }
  }

  const userPermissions = store.getters['auth/userPermissions'] || []
  const isOwner = !!store.getters['auth/isOwner']

  // 4. CHECK PERMISSIONS
  if (to.meta.resource) {
    const hasPermission = isOwner || userPermissions.includes(to.meta.resource)

    if (!hasPermission) {
      router.app.hideLoading()
      return next({ name: 'profile' })
    }
  }

  router.app.hideLoading()
  return next()
})

export default router
